/**
 * NoteComposer - Input for adding manual notes
 */

import React, { useState, useRef, useEffect } from 'react';
import { Send, Loader2 } from 'lucide-react';

interface NoteComposerProps {
  onSubmit: (content: string) => Promise<void>;
  isSubmitting?: boolean;
  placeholder?: string;
  disabled?: boolean;
}

export function NoteComposer({
  onSubmit,
  isSubmitting = false,
  placeholder = 'Add a note...',
  disabled = false,
}: NoteComposerProps) {
  const [content, setContent] = useState('');
  const [isFocused, setIsFocused] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Auto-resize textarea
  useEffect(() => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = 'auto';
      textarea.style.height = `${Math.min(textarea.scrollHeight, 120)}px`;
    }
  }, [content]);

  const handleSubmit = async () => {
    const trimmedContent = content.trim();
    if (!trimmedContent || isSubmitting || disabled) return;

    try {
      await onSubmit(trimmedContent);
      setContent('');
      // Reset textarea height
      if (textareaRef.current) {
        textareaRef.current.style.height = 'auto';
      }
    } catch (error) {
      console.error('Failed to add note:', error);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    // Submit on Cmd/Ctrl + Enter
    if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
      e.preventDefault();
      handleSubmit();
    }
  };

  const canSubmit = content.trim().length > 0 && !isSubmitting && !disabled;

  return (
    <div
      className={`
        rounded-lg border transition-colors
        ${isFocused ? 'border-blue-400 ring-1 ring-blue-100' : 'border-slate-200'}
        ${disabled ? 'opacity-50 bg-slate-50' : 'bg-white'}
      `}
    >
      <div className="p-2">
        <textarea
          ref={textareaRef}
          value={content}
          onChange={(e) => setContent(e.target.value)}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          disabled={disabled || isSubmitting}
          rows={1}
          className="
            w-full resize-none border-0 bg-transparent
            text-sm text-slate-700 placeholder-slate-400
            focus:outline-none focus:ring-0
            disabled:cursor-not-allowed
          "
          style={{ minHeight: '24px', maxHeight: '120px' }}
        />
      </div>

      {/* Footer with hint and submit button */}
      <div className="flex items-center justify-between px-2 pb-2">
        <span className="text-[10px] text-slate-400">
          {isFocused && content.trim() && 'Press Cmd/Ctrl + Enter to send'}
        </span>

        <button
          onClick={handleSubmit}
          disabled={!canSubmit}
          className={`
            p-1.5 rounded-md transition-colors
            ${canSubmit
              ? 'bg-blue-500 text-white hover:bg-blue-600'
              : 'bg-slate-100 text-slate-400 cursor-not-allowed'
            }
          `}
          title="Add note"
        >
          {isSubmitting ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <Send className="w-4 h-4" />
          )}
        </button>
      </div>
    </div>
  );
}

export default NoteComposer;
